import { applyDiscountCode as applyDiscountCodeApi } from './api';
import { createAppSlice } from './createAppSlice';
import { parseApiError } from './errors';

import type { DiscountCodeError } from './api';
import type { PayloadAction } from '@reduxjs/toolkit';

type DiscountErrorCode = DiscountCodeError['errorCode'] | 'UNKNOWN_ERROR';

interface DiscountRejectedPayload {
    errorCode: DiscountErrorCode;
    message: string;
    unrecognizedCode?: string;
}

// Compile-time exhaustiveness: Record<K, true> requires ALL union members as keys.
// Adding a code to DiscountCodeError['errorCode'] in api.ts without adding a key
// here causes a compile error. The array is derived from the map for runtime use.
const DISCOUNT_ERROR_CODE_MAP: Record<DiscountCodeError['errorCode'], true> = {
    EXPIRED_DISCOUNT_CODE: true,
    INVALID_CODE: true,
};
const DISCOUNT_ERROR_CODES = Object.keys(DISCOUNT_ERROR_CODE_MAP) as readonly DiscountCodeError['errorCode'][];

const KNOWN_DISCOUNT_ERROR_CODES: ReadonlySet<DiscountCodeError['errorCode']> = new Set(DISCOUNT_ERROR_CODES);

/** Extracts a structured discount error from an unknown thrown value. */
function parseDiscountError(error: unknown): DiscountRejectedPayload {
    return parseApiError(error, {
        expectedCategories: ['DISCOUNT_CODE_ERROR'] satisfies DiscountCodeError['errorCategory'][],
        knownCodes: KNOWN_DISCOUNT_ERROR_CODES,
    });
}

/** `code` is orthogonal to status — a code may be set (via discountCodeSet or
 *  URL param) before any API call, and persists across failed attempts so the
 *  UI can display which code was rejected.
 *
 *  `requestedOrderId` is the staleness guard: if a fulfilled/rejected action's
 *  orderId (from meta.arg) doesn't match, the response arrived out-of-order
 *  and is discarded. */
type DiscountState =
    | { code: string | undefined; errorCode: DiscountErrorCode; requestedOrderId: string; status: 'failed' }
    | { code: string | undefined; errorCode: undefined; requestedOrderId: string; status: 'pending' }
    | { code: string; errorCode: undefined; requestedOrderId: string; status: 'succeeded' }
    | { code: string | undefined; errorCode: undefined; requestedOrderId: string | undefined; status: 'idle' };

const initialState = {
    code: undefined,
    errorCode: undefined,
    requestedOrderId: undefined,
    status: 'idle',
} satisfies DiscountState as DiscountState;

const discountSlice = createAppSlice({
    initialState,
    name: 'discount',
    reducers: (create) => ({
        applyDiscountCode: create.asyncThunk(
            async (
                { discountCode, orderId }: { discountCode: string; orderId: string },
                { rejectWithValue },
            ) => {
                try {
                    await applyDiscountCodeApi(orderId, discountCode);
                    return { discountCode };
                } catch (error: unknown) {
                    // See parseApiError (errors.ts) for how errorCode is extracted
                    // from the service error's responseBody.
                    return rejectWithValue(parseDiscountError(error));
                }
            },
            {
                fulfilled: (state, action) => {
                    // Staleness guard: discard if a newer request has been made
                    if (state.requestedOrderId
                        && action.meta.arg.orderId !== state.requestedOrderId) {
                        return;
                    }
                    // eslint-disable-next-line consistent-return -- void = keep state (stale guard), object = replace state
                    return {
                        code: action.payload.discountCode,
                        errorCode: undefined,
                        requestedOrderId: state.requestedOrderId ?? action.meta.arg.orderId,
                        status: 'succeeded',
                    } satisfies DiscountState;
                },
                pending: (state, action) => ({
                    code: state.code,
                    errorCode: undefined,
                    requestedOrderId: action.meta.arg.orderId,
                    status: 'pending',
                }) satisfies DiscountState,
                rejected: (state, action) => {
                    // Staleness guard: discard if a newer request has been made
                    if (state.requestedOrderId
                        && action.meta.arg.orderId !== state.requestedOrderId) {
                        return;
                    }
                    const errorCode: DiscountErrorCode = action.meta.rejectedWithValue
                        ? (action.payload as DiscountRejectedPayload | undefined)?.errorCode ?? 'UNKNOWN_ERROR'
                        : 'UNKNOWN_ERROR';
                    // eslint-disable-next-line consistent-return -- void = keep state (stale guard), object = replace state
                    return {
                        code: state.code,
                        errorCode,
                        requestedOrderId: state.requestedOrderId ?? action.meta.arg.orderId,
                        status: 'failed',
                    } satisfies DiscountState;
                },
            },
        ),
        discountCleared: create.reducer(() => initialState),
        discountCodeSet: create.reducer((state, action: PayloadAction<string>) => ({
            code: action.payload,
            errorCode: undefined,
            requestedOrderId: state.requestedOrderId,
            status: 'idle',
        }) satisfies DiscountState),
        discountSyncedFromServer: create.reducer(
            (state, action: PayloadAction<{ code: string; orderId: string }>) => ({
                code: action.payload.code,
                errorCode: undefined,
                requestedOrderId: action.payload.orderId,
                status: 'succeeded',
            }) satisfies DiscountState,
        ),
    }),
    selectors: {
        selectDiscountCode: (state) => state.code,
        selectDiscountErrorCode: (state) => state.errorCode,
        selectDiscountStatus: (state) => state.status,
        selectHasDiscount: (state) => state.status === 'succeeded',
    },
});

export type { DiscountErrorCode, DiscountRejectedPayload, DiscountState };
export { DISCOUNT_ERROR_CODES, parseDiscountError };
export const {
    applyDiscountCode, discountCleared, discountCodeSet, discountSyncedFromServer,
} = discountSlice.actions;
export const discountReducer = discountSlice.reducer;
export const getDiscountSelectors = discountSlice.getSelectors;
export const {
    selectDiscountCode,
    selectDiscountErrorCode,
    selectDiscountStatus,
    selectHasDiscount,
} = discountSlice.selectors;
